/** @format */

import {
  DeleteOutlined,
  EditOutlined,
  MinusOutlined,
  MoreOutlined,
  PlusOutlined,
  StarFilled,
  UserAddOutlined,
} from "@ant-design/icons";
import { Dropdown, Menu, Tooltip } from "antd";
import React, { Fragment, memo, useState } from "react";
import { Handle, NodeProps, Position } from "react-flow-renderer";
import { DRAG_MIME, useOrgChart } from "../context";
import { OrgAvatar } from "../partials/OrgAvatar";
import { TPersonNodeData } from "../utils/layout";
import { fullNameOf, META_SEPARATOR, showsStaffPill } from "../utils/people";

const stop = (e: React.SyntheticEvent): void => e.stopPropagation();

const PersonNodeComponent = ({ data }: NodeProps<TPersonNodeData>): JSX.Element | null => {
  const {
    canUpdate,
    draggingUserId,
    blockedDropIds,
    onOpenPerson,
    onAddCollaborator,
    onToggleCollapse,
    onRemovePerson,
    onDropUser,
  } = useOrgChart();
  const [dropOver, setDropOver] = useState(false);

  if (!data?.person) return null;

  const { person, height, directCount, hiddenCount, collapsed, highlighted, dimmed, focused } = data;
  const fullName = fullNameOf(person);
  const isStaff = person.is_staff;

  // Ruolo e pillole scorrono come testo: le pillole seguono l'ultima parola del ruolo
  const metaParts = [
    person.role_name && (
      <span key="role" className="org-node__role">
        {person.role_name}
      </span>
    ),
    showsStaffPill(person) && (
      <span key="staff" className="org-pill org-pill--staff">
        Staff
      </span>
    ),
    person.is_disabled && (
      <span key="disabled" className="org-pill">
        Disabilitato
      </span>
    ),
  ].filter(Boolean);

  // Lo staff non riceve collaboratori (il layout non li disegna)
  const canReceive =
    canUpdate &&
    draggingUserId !== null &&
    !isStaff &&
    draggingUserId !== person.id &&
    !blockedDropIds.has(person.id);

  const className = [
    "org-node",
    isStaff && "org-node--staff",
    person.is_disabled && "org-node--disabled",
    highlighted && "org-node--match",
    focused && "org-node--focus",
    dimmed && "org-node--dim",
    canReceive && "org-node--droppable",
    canReceive && dropOver && "org-node--drop-over",
  ]
    .filter(Boolean)
    .join(" ");

  const onDragOver = (e: React.DragEvent): void => {
    if (!canReceive) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    if (!dropOver) setDropOver(true);
  };

  const onDrop = (e: React.DragEvent): void => {
    if (!canReceive) return;
    e.preventDefault();
    e.stopPropagation();
    setDropOver(false);
    const userId = Number(e.dataTransfer.getData(DRAG_MIME) || draggingUserId);
    if (userId) onDropUser(person.id, userId);
  };

  const menu = (
    <Menu
      onClick={({ key, domEvent }): void => {
        domEvent.stopPropagation();
        if (key === "edit") onOpenPerson(person.id);
        if (key === "add") onAddCollaborator(person.id);
        if (key === "remove") onRemovePerson(person.id);
      }}
      items={[
        { key: "edit", icon: <EditOutlined />, label: "Modifica collocazione" },
        ...(!isStaff
          ? [{ key: "add", icon: <UserAddOutlined />, label: "Aggiungi collaboratore" }]
          : []),
        { type: "divider" as const },
        { key: "remove", icon: <DeleteOutlined />, label: "Togli dall'organigramma", danger: true },
      ]}
    />
  );

  return (
    <div
      className={className}
      onClick={(): void => onOpenPerson(person.id)}
      onDragOver={onDragOver}
      onDragLeave={(): void => setDropOver(false)}
      onDrop={onDrop}
      style={{ minHeight: height }}
      title={[fullName, person.role_name].filter(Boolean).join("\n")}
      role="button"
      tabIndex={0}
      onKeyDown={(e): void => {
        if (e.key === "Enter") onOpenPerson(person.id);
      }}
    >
      {/* Lo staff si aggancia da destra, la linea gerarchica dall'alto */}
      {/* Se cambi l'ingombro del contenuto, allinea probeOf in utils/measureCards.ts */}
      <Handle
        type="target"
        position={isStaff ? Position.Right : Position.Top}
        className="org-handle"
        isConnectable={false}
      />

      <OrgAvatar person={person} size={38} className="org-node__avatar" />

      <div className="org-node__text">
        <div className="org-node__name">
          <span className="org-node__name-text">{fullName}</span>
          {person.is_coordinator && (
            <Tooltip title="Coordinatore">
              <StarFilled className="org-node__coordinator" aria-label="Coordinatore" />
            </Tooltip>
          )}
        </div>
        <div className="org-node__meta">
          {metaParts.map((part, i) => (
            <Fragment key={i}>
              {i > 0 && META_SEPARATOR}
              {part}
            </Fragment>
          ))}
        </div>
      </div>

      {canUpdate && (
        <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
          <button
            type="button"
            className="org-node__more nodrag nopan org-no-export"
            onClick={stop}
            aria-label={`Azioni per ${fullName}`}
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      )}

      <Handle type="source" position={Position.Bottom} className="org-handle" isConnectable={false} />

      {canUpdate && !isStaff && (
        <Tooltip title="Aggiungi collaboratore" mouseEnterDelay={0.4}>
          <button
            type="button"
            className="org-node__add nodrag nopan org-no-export"
            onClick={(e): void => {
              e.stopPropagation();
              onAddCollaborator(person.id);
            }}
            aria-label={`Aggiungi un collaboratore a ${fullName}`}
          >
            <PlusOutlined />
          </button>
        </Tooltip>
      )}

      {directCount > 0 && !isStaff && (
        <Tooltip title={collapsed ? "Espandi il ramo" : "Comprimi il ramo"} mouseEnterDelay={0.4}>
          <button
            type="button"
            className={`org-node__toggle nodrag nopan${collapsed ? " is-collapsed" : " org-no-export"}`}
            onClick={(e): void => {
              e.stopPropagation();
              onToggleCollapse(person.id);
            }}
            aria-label={collapsed ? `Espandi il ramo di ${fullName}` : `Comprimi il ramo di ${fullName}`}
          >
            {collapsed ? `+${hiddenCount}` : <MinusOutlined />}
          </button>
        </Tooltip>
      )}

      {canReceive && dropOver && (
        <div className="org-node__drop-label org-no-export">Rilascia per aggiungere al team</div>
      )}
    </div>
  );
};

export const PersonNode = memo(PersonNodeComponent);
PersonNode.displayName = "PersonNode";
